import { Link, router, useForm, usePage } from '@inertiajs/react';
import { useId, useState } from 'react';
import type { FormEvent } from 'react';

import { ErrorSummary } from '@/components/public/forms/ErrorSummary';
import FormActions from '@/components/public/forms/FormActions';
import { SubmissionReceipt } from '@/components/public/forms/SubmissionReceipt';
import DatePicker from '@/components/public/ui/DatePicker';
import Select from '@/components/public/ui/Select';
import TimePicker from '@/components/public/ui/TimePicker';
import { trackUmamiEvent } from '@/lib/analytics';
import { buildErrorSummaryItems } from '@/lib/errorSummary';
import { inputClass, inputErrorClass, sectionLabel } from '@/lib/formStyles';
import {
  futureDateError,
  timeRangeError,
  todayISODate,
  validateEmail,
  validatePhonePT,
} from '@/lib/validators';

type FlashProps = { flash: { notice?: string; alert?: string } };

type Payload = {
  name: string;
  documentType: string;
  documentNumber: string;
  documentTypeOther: string;
  email: string;
  phone: string;
  entity: string;
  requesterAddress: string;
  activityType: string;
  activityLocation: string;
  date: string;
  startTime: string;
  endTime: string;
  noiseSource: string;
  noiseSourceOther: string;
  mitigationMeasures: string;
  responsibleName: string;
  responsiblePhone: string;
};

const FIELD_LABELS: Record<string, string> = {
  name: 'Nome completo',
  documentType: 'Tipo de documento',
  documentNumber: 'N.º do documento',
  documentTypeOther: 'Tipo de documento',
  email: 'Correio eletrónico',
  phone: 'Telefone',
  entity: 'Entidade',
  requesterAddress: 'Morada do requerente',
  activityType: 'Tipo de atividade',
  activityLocation: 'Local da atividade',
  date: 'Data',
  startTime: 'Hora de início',
  endTime: 'Hora de fim',
  noiseSource: 'Fonte de ruído',
  noiseSourceOther: 'Fonte de ruído',
  mitigationMeasures: 'Medidas de mitigação',
  responsibleName: 'Responsável no local',
  responsiblePhone: 'Telefone do responsável',
  consent: 'Consentimento',
  files: 'Anexos',
  type: 'Tipo de pedido',
};

const FIELD_ANCHORS = new Set([
  'name',
  'documentType',
  'documentNumber',
  'documentTypeOther',
  'email',
  'phone',
  'entity',
  'requesterAddress',
  'activityType',
  'activityLocation',
  'date',
  'startTime',
  'endTime',
  'noiseSource',
  'noiseSourceOther',
  'mitigationMeasures',
  'responsibleName',
  'responsiblePhone',
]);

export default function NoisePermitForm() {
  const { flash } = usePage<FlashProps>().props;
  const idPrefix = useId();
  const fid = (name: string) => `${idPrefix}-${name}`;
  const errId = (name: string) => `${idPrefix}-${name}-error`;

  const form = useForm<{
    payload: Payload;
    files: File[];
    consentLegal: boolean;
    consentData: boolean;
    consented_at: string;
  }>({
    payload: {
      name: '',
      documentType: '',
      documentNumber: '',
      documentTypeOther: '',
      email: '',
      phone: '',
      entity: '',
      requesterAddress: '',
      activityType: '',
      activityLocation: '',
      date: '',
      startTime: '',
      endTime: '',
      noiseSource: '',
      noiseSourceOther: '',
      mitigationMeasures: '',
      responsibleName: '',
      responsiblePhone: '',
    },
    files: [],
    consentLegal: false,
    consentData: false,
    consented_at: '',
  });

  const { data, setData, processing, errors: rawErrors, reset } = form;
  const errors = rawErrors as unknown as Record<string, string | undefined>;

  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});

  const set =
    (field: keyof Payload) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setData('payload', { ...data.payload, [field]: e.target.value });

  const validateField = (field: string, value: string): string | null => {
    if (!value) return null;
    if ((field === 'phone' || field === 'responsiblePhone') && !validatePhonePT(value))
      return 'Número de telefone inválido.';
    if (field === 'email' && !validateEmail(value)) return 'Correio eletrónico inválido.';
    if (field === 'date') return futureDateError(value);
    return null;
  };

  const handleBlur = (field: keyof Payload) => () => {
    const value = String(data.payload[field]);
    const err = validateField(field, value);
    setClientErrors((prev) => {
      const next = { ...prev };
      if (err) next[field] = err;
      else delete next[field];
      return next;
    });
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    const phoneErr = validateField('phone', data.payload.phone);
    if (phoneErr) errs.phone = phoneErr;
    const respPhoneErr = validateField('responsiblePhone', data.payload.responsiblePhone);
    if (respPhoneErr) errs.responsiblePhone = respPhoneErr;
    const emailErr = validateField('email', data.payload.email);
    if (emailErr) errs.email = emailErr;
    const dateErr = validateField('date', data.payload.date);
    if (dateErr) errs.date = dateErr;
    const timeErr = timeRangeError(data.payload.startTime, data.payload.endTime);
    if (timeErr) errs.endTime = timeErr;
    if (Object.keys(errs).length > 0) {
      setClientErrors(errs);
      trackUmamiEvent('form_submit_blocked', {
        form_type: 'noise_permit',
        error_count: Object.keys(errs).length,
      });
      return;
    }
    form.transform((d) => ({
      type: 'noise_permit',
      consent: 'true',
      consented_at: new Date().toISOString(),
      payload: d.payload,
      files: d.files,
    }));
    trackUmamiEvent('service_request_submit', {
      service_type: 'noise_permit',
      has_files: data.files.length > 0,
    });
    form.post('/service-requests', { forceFormData: true });
  };

  const fieldErr = (field: string) => clientErrors[field] || errors[field];
  const errorItems = buildErrorSummaryItems({ ...errors, ...clientErrors }, FIELD_LABELS, (key) =>
    FIELD_ANCHORS.has(key) ? `#${fid(key)}` : undefined,
  );

  const p = data.payload;
  const isFormValid =
    !!p.name &&
    !!p.documentType &&
    !!p.documentNumber &&
    (p.documentType !== 'outro' || !!p.documentTypeOther.trim()) &&
    !!p.email &&
    !!p.phone &&
    !!p.requesterAddress &&
    !!p.activityType &&
    !!p.activityLocation &&
    !!p.date &&
    !!p.startTime &&
    !!p.endTime &&
    !!p.noiseSource &&
    (p.noiseSource !== 'outro' || !!p.noiseSourceOther.trim()) &&
    !!p.mitigationMeasures &&
    !!p.responsibleName &&
    !!p.responsiblePhone &&
    data.consentLegal &&
    data.consentData &&
    Object.keys(clientErrors).length === 0;

  if (flash?.notice) {
    return (
      <SubmissionReceipt
        formType="noise_permit"
        title="Pedido submetido à Junta"
        message={flash.notice}
        steps={[
          { label: 'Registo', description: 'O pedido ficou guardado com data e consentimento.' },
          { label: 'Análise', description: 'Os serviços confirmam o enquadramento e o horário.' },
          {
            label: 'Contacto',
            description: 'Os serviços entrarão em contacto se forem necessários esclarecimentos.',
          },
        ]}
        actions={[
          {
            label: 'Submeter outro pedido',
            onClick: () => {
              reset();
              setClientErrors({});
              router.get(window.location.pathname, {}, { replace: true, preserveScroll: true });
            },
          },
          { label: 'Voltar aos serviços', href: '/services', variant: 'primary' },
        ]}
      />
    );
  }

  return (
    <div className="space-y-4">
      <form onSubmit={submit} className="space-y-8">
        <ErrorSummary items={errorItems} />

        {/* Requerente */}
        <div>
          <span className={sectionLabel('primary')}>Requerente</span>
          <div className="mb-4">
            <label
              htmlFor={fid('name')}
              className="text-on-surface mb-1.5 block text-sm font-medium"
            >
              Nome completo
            </label>
            <input
              id={fid('name')}
              className={inputClass}
              value={p.name}
              onChange={set('name')}
              placeholder="Ex: Carlos Mendes"
              required
            />
          </div>
          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label
                htmlFor={fid('documentType')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Tipo de Documento
              </label>
              <Select
                id={fid('documentType')}
                value={p.documentType}
                onChange={(e) => {
                  const next = e.target.value;
                  setData('payload', {
                    ...p,
                    documentType: next,
                    documentTypeOther: next === 'outro' ? p.documentTypeOther : '',
                  });
                }}
                required
              >
                <option value="">Selecionar</option>
                <option value="cc">Cartão de Cidadão</option>
                <option value="passaporte">Passaporte</option>
                <option value="residencia">Título de Residência</option>
                <option value="outro">Outro</option>
              </Select>
            </div>
            <div>
              <label
                htmlFor={fid('documentNumber')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                N.º do documento
              </label>
              <input
                id={fid('documentNumber')}
                className={inputClass}
                value={p.documentNumber}
                onChange={set('documentNumber')}
                placeholder="Ex: 12345678 9 ZZ4"
                required
              />
            </div>
          </div>
          {p.documentType === 'outro' && (
            <div className="mb-4">
              <label
                htmlFor={fid('documentTypeOther')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Especifique o tipo de documento
              </label>
              <input
                id={fid('documentTypeOther')}
                className={inputClass}
                value={p.documentTypeOther}
                onChange={set('documentTypeOther')}
                placeholder="Ex: Carta de Condução, Passaporte estrangeiro…"
                required
              />
            </div>
          )}
          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label
                htmlFor={fid('email')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Correio eletrónico
              </label>
              <input
                id={fid('email')}
                type="email"
                className={fieldErr('email') ? inputErrorClass : inputClass}
                value={p.email}
                onChange={set('email')}
                onBlur={handleBlur('email')}
                aria-invalid={!!fieldErr('email')}
                aria-describedby={fieldErr('email') ? errId('email') : undefined}
                placeholder="email@exemplo.pt"
                required
              />
              {fieldErr('email') && (
                <p id={errId('email')} className="text-error mt-1 text-xs">
                  {fieldErr('email')}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor={fid('phone')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Telefone
              </label>
              <input
                id={fid('phone')}
                type="tel"
                className={fieldErr('phone') ? inputErrorClass : inputClass}
                value={p.phone}
                onChange={set('phone')}
                onBlur={handleBlur('phone')}
                aria-invalid={!!fieldErr('phone')}
                aria-describedby={fieldErr('phone') ? errId('phone') : undefined}
                placeholder="+351 9XX XXX XXX"
                required
              />
              {fieldErr('phone') && (
                <p id={errId('phone')} className="text-error mt-1 text-xs">
                  {fieldErr('phone')}
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label
                htmlFor={fid('entity')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Entidade / Associação{' '}
                <span className="text-on-surface-light font-normal">(opcional)</span>
              </label>
              <input
                id={fid('entity')}
                className={inputClass}
                value={p.entity}
                onChange={set('entity')}
                placeholder="Ex: Clube Desportivo"
              />
            </div>
            <div>
              <label
                htmlFor={fid('requesterAddress')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Morada do Requerente
              </label>
              <input
                id={fid('requesterAddress')}
                className={inputClass}
                value={p.requesterAddress}
                onChange={set('requesterAddress')}
                placeholder="Rua, n.º, código postal"
                required
              />
            </div>
          </div>
        </div>

        {/* Atividade */}
        <div>
          <span className={sectionLabel('primary')}>Atividade</span>
          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label
                htmlFor={fid('activityType')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Tipo de Atividade
              </label>
              <input
                id={fid('activityType')}
                className={inputClass}
                value={p.activityType}
                onChange={set('activityType')}
                placeholder="Ex: Festa Popular, Concerto"
                required
              />
            </div>
            <div>
              <label
                htmlFor={fid('activityLocation')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Local da Atividade
              </label>
              <input
                id={fid('activityLocation')}
                className={inputClass}
                value={p.activityLocation}
                onChange={set('activityLocation')}
                placeholder="Ex: Largo da Igreja"
                required
              />
            </div>
          </div>
          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label
                htmlFor={fid('date')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Data
              </label>
              <DatePicker
                id={fid('date')}
                value={p.date}
                onChange={(value) => {
                  setData('payload', { ...p, date: value });
                  const err = validateField('date', value);
                  setClientErrors((prev) => {
                    const next = { ...prev };
                    if (err) next.date = err;
                    else delete next.date;
                    return next;
                  });
                }}
                min={todayISODate()}
                hasError={!!fieldErr('date')}
                aria-invalid={!!fieldErr('date')}
                aria-describedby={fieldErr('date') ? errId('date') : undefined}
                required
              />
              {fieldErr('date') && (
                <p id={errId('date')} className="text-error mt-1 text-xs">
                  {fieldErr('date')}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor={fid('startTime')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Hora de Início
              </label>
              <TimePicker
                id={fid('startTime')}
                value={p.startTime}
                onChange={(value) => setData('payload', { ...p, startTime: value })}
                required
              />
            </div>
            <div>
              <label
                htmlFor={fid('endTime')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Hora de Fim
              </label>
              <TimePicker
                id={fid('endTime')}
                value={p.endTime}
                onChange={(value) => setData('payload', { ...p, endTime: value })}
                hasError={!!fieldErr('endTime')}
                aria-invalid={!!fieldErr('endTime')}
                aria-describedby={fieldErr('endTime') ? errId('endTime') : undefined}
                required
              />
              {fieldErr('endTime') && (
                <p id={errId('endTime')} className="text-error mt-1 text-xs">
                  {fieldErr('endTime')}
                </p>
              )}
            </div>
          </div>
          <div>
            <label
              htmlFor={fid('noiseSource')}
              className="text-on-surface mb-1.5 block text-sm font-medium"
            >
              Fonte de Ruído
            </label>
            <Select
              id={fid('noiseSource')}
              value={p.noiseSource}
              onChange={(e) => {
                const next = e.target.value;
                setData('payload', {
                  ...p,
                  noiseSource: next,
                  noiseSourceOther: next === 'outro' ? p.noiseSourceOther : '',
                });
              }}
              required
            >
              <option value="">Selecionar</option>
              <option value="amplificacao">Amplificação Musical</option>
              <option value="fogo-artificio">Fogo de Artifício</option>
              <option value="obras">Obras / Construção</option>
              <option value="geradores">Geradores</option>
              <option value="pa-system">Sistema PA / Megafone</option>
              <option value="outro">Outro</option>
            </Select>
          </div>
          {p.noiseSource === 'outro' && (
            <div className="mt-4">
              <label
                htmlFor={fid('noiseSourceOther')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Especifique a fonte de ruído
              </label>
              <input
                id={fid('noiseSourceOther')}
                className={inputClass}
                value={p.noiseSourceOther}
                onChange={set('noiseSourceOther')}
                placeholder="Ex: DJ ao vivo, sistema de som móvel…"
                required
              />
            </div>
          )}
        </div>

        {/* Medidas e Responsável */}
        <div>
          <span className={sectionLabel('primary')}>Medidas de Minimização e Responsável</span>
          <div className="mb-4">
            <label
              htmlFor={fid('mitigationMeasures')}
              className="text-on-surface mb-1.5 block text-sm font-medium"
            >
              Descreva as medidas previstas para minimizar o impacto sonoro
            </label>
            <textarea
              id={fid('mitigationMeasures')}
              className={`${inputClass} resize-none`}
              rows={4}
              value={p.mitigationMeasures}
              onChange={set('mitigationMeasures')}
              placeholder="Ex: Limitação de volume a partir das 24h, orientação dos altifalantes para longe de habitações..."
              required
            />
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label
                htmlFor={fid('responsibleName')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Responsável no Local
              </label>
              <input
                id={fid('responsibleName')}
                className={inputClass}
                value={p.responsibleName}
                onChange={set('responsibleName')}
                placeholder="Nome do responsável"
                required
              />
            </div>
            <div>
              <label
                htmlFor={fid('responsiblePhone')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Contacto do Responsável
              </label>
              <input
                id={fid('responsiblePhone')}
                type="tel"
                className={fieldErr('responsiblePhone') ? inputErrorClass : inputClass}
                value={p.responsiblePhone}
                onChange={set('responsiblePhone')}
                onBlur={handleBlur('responsiblePhone')}
                aria-invalid={!!fieldErr('responsiblePhone')}
                aria-describedby={
                  fieldErr('responsiblePhone') ? errId('responsiblePhone') : undefined
                }
                placeholder="+351 9XX XXX XXX"
                required
              />
              {fieldErr('responsiblePhone') && (
                <p id={errId('responsiblePhone')} className="text-error mt-1 text-xs">
                  {fieldErr('responsiblePhone')}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Consents */}
        <div className="space-y-3">
          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={data.consentLegal}
              onChange={(e) => setData('consentLegal', e.target.checked)}
              className="mt-1 rounded"
              required
            />
            <span className="text-on-surface-light text-sm leading-relaxed">
              Declaro que a atividade cumprirá a legislação vigente em matéria de ruído (Decreto-Lei
              n.º 9/2007) e comprometo-me a respeitar os limites horários e de volume autorizados.
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={data.consentData}
              onChange={(e) => setData('consentData', e.target.checked)}
              className="mt-1 rounded"
              required
            />
            <span className="text-on-surface-light text-sm leading-relaxed">
              Tomei conhecimento do tratamento dos meus dados pessoais para efeitos deste pedido,
              nos termos da{' '}
              <Link href="/privacy" className="text-primary underline">
                política de privacidade
              </Link>
              .
            </span>
          </label>
        </div>

        <FormActions
          submitLabel="Submeter pedido"
          submittingLabel="A submeter…"
          submittingHint="A enviar os dados de forma segura"
          processing={processing}
          disabled={!isFormValid}
          onReset={() => {
            reset();
            setClientErrors({});
          }}
        />
      </form>
    </div>
  );
}
